import { useCallback, useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { Alert, Button, DatePicker, Drawer, Input, Skeleton, Space, Typography, message } from 'antd';
import { CheckCircleFilled, CloseCircleFilled } from '@ant-design/icons';
import dayjs, { type Dayjs } from 'dayjs';
import {
  decideRelease,
  getAgentRelease,
  type AgentRelease,
  type ReleaseDecisionKind,
  type ReleaseState,
} from '@/api/validationRuns';
import { SectionCard } from '@/components/SectionCard';
import { StatusPill, type PillTone } from '@/components/StatusPill';
import { useStore } from '@/stores';
import { colors } from '@/theme/tokens';

const { Text } = Typography;

// ─────────────────────────────────────────────────────────────────────────────
// ReleasePanel — RELEASE-01 / STUDIO-EVAL-07 (workspace plan §10.5). The server's checklist for the agent's CURRENT
// version, where that version stands, what changed since a version that was approved, and the decision history.
// Approve is offered only when every item is met; otherwise an exception needs a reason, an expiry (≤ 30 days) and a
// follow-up, and it lapses on its own. This panel records decisions; making a version live is a separate step, and
// any change to what the model is given needs a new decision.
// ─────────────────────────────────────────────────────────────────────────────

const STATE: Record<ReleaseState, { label: string; tone: PillTone }> = {
  NotDecided: { label: 'Not approved for release', tone: 'slate' },
  Approved: { label: 'Approved for release', tone: 'green' },
  Exception: { label: 'Exceptional release', tone: 'amber' },
  ExceptionExpired: { label: 'Exception expired', tone: 'red' },
  Rejected: { label: 'Rejected for release', tone: 'red' },
  Revoked: { label: 'Approval revoked', tone: 'slate' },
  ApprovalNoLongerHolds: { label: 'Approval no longer holds', tone: 'red' },
  ExceptionNoLongerCovers: { label: 'Exception no longer covers it', tone: 'red' },
};

const DECISION_WORDS: Record<ReleaseDecisionKind, string> = {
  Approved: 'Approved',
  Rejected: 'Rejected',
  Exception: 'Exception',
  Revoked: 'Revoked',
};

export const ReleasePanel = observer(function ReleasePanel({ agentId, refreshToken }: { agentId: number; refreshToken: number }) {
  const { themeStore } = useStore();
  const isDark = themeStore.mode === 'dark';
  const [release, setRelease] = useState<AgentRelease | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [deciding, setDeciding] = useState<ReleaseDecisionKind | null>(null);
  const [reason, setReason] = useState('');
  const [followUp, setFollowUp] = useState('');
  const [expires, setExpires] = useState<Dayjs | null>(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(
    (signal?: AbortSignal) => {
      getAgentRelease(agentId, signal)
        .then((r) => {
          if (signal?.aborted) return;
          setRelease(r);
          setError(null);
        })
        .catch((err: Error) => !signal?.aborted && setError(err.message));
    },
    [agentId],
  );

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [load, refreshToken]);

  const open = (kind: ReleaseDecisionKind) => {
    setReason('');
    setFollowUp('');
    setExpires(kind === 'Exception' ? dayjs().add(7, 'day') : null);
    setDeciding(kind);
  };

  const submit = async () => {
    if (!deciding || !release) return;
    setSaving(true);
    try {
      const updated = await decideRelease(agentId, {
        decision: deciding,
        reason: reason.trim(),
        expiresUtc: deciding === 'Exception' && expires ? expires.endOf('day').toISOString() : undefined,
        followUp: deciding === 'Exception' ? followUp.trim() : undefined,
        expectedCandidateHash: release.currentCandidateHash,
      });
      setRelease(updated);
      setDeciding(null);
      void message.success('Decision recorded.');
    } catch (err) {
      void message.error((err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  if (error) return <Alert type="error" showIcon title="Could not load the release checklist" description={error} />;
  if (!release)
    return (
      <SectionCard>
        <Skeleton active paragraph={{ rows: 3 }} />
      </SectionCard>
    );

  const s = STATE[release.state];
  const live =
    release.state === 'Approved' ||
    release.state === 'Exception' ||
    release.state === 'ApprovalNoLongerHolds' ||
    release.state === 'ExceptionNoLongerCovers';
  const divider = isDark ? colors.slate[700] : colors.slate[200];

  return (
    <SectionCard
      title={
        <Space size={8} wrap>
          <span>Release</span>
          <StatusPill tone={s.tone}>{s.label}</StatusPill>
          {release.state === 'Exception' && release.currentDecision?.expiresUtc && (
            <Text type="secondary" style={{ fontSize: 12, fontWeight: 400 }}>{`until ${dayjs(release.currentDecision.expiresUtc).format('D MMM YYYY')}`}</Text>
          )}
        </Space>
      }
      subtitle={`For this exact version of the agent (${release.currentCandidateHash ? release.currentCandidateHash.slice(0, 10) : 'unknown'}), under ${release.policyVersion}. Any change to its instructions, skills, tools, model or knowledge needs a new decision.`}
    >
      {release.noLongerHoldsReason && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
          title="The recorded decision no longer holds, so this version may not go live"
          description={release.noLongerHoldsReason}
        />
      )}

      {release.changedSinceApprovedVersion.length > 0 && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          title="An earlier version was approved; this one has changed since"
          description={
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              {release.changedSinceApprovedVersion.map((c) => (
                <li key={c}>{c}</li>
              ))}
            </ul>
          }
        />
      )}

      <div style={{ marginBottom: 12 }}>
        {release.checklistItems.map((item, i) => (
          <div key={item.key} style={{ display: 'flex', gap: 10, padding: '8px 0', borderTop: i === 0 ? undefined : `1px solid ${divider}` }}>
            <span style={{ color: item.met ? colors.green[600] : colors.red[600], paddingTop: 2 }}>
              {item.met ? <CheckCircleFilled aria-label="Met" /> : <CloseCircleFilled aria-label="Not met" />}
            </span>
            <div style={{ minWidth: 0 }}>
              <Text strong>{item.requirement}</Text>
              <Text type="secondary" style={{ display: 'block', fontSize: 12, wordBreak: 'break-word' }}>{item.detail}</Text>
            </div>
          </div>
        ))}
      </div>

      <Space size={[8, 8]} wrap style={{ marginBottom: 12 }}>
        <Button type="primary" disabled={!release.eligible || release.state === 'Approved'} onClick={() => open('Approved')}>
          Approve release
        </Button>
        {!release.eligible && <Button onClick={() => open('Exception')}>Record an exception…</Button>}
        <Button danger onClick={() => open('Rejected')}>Reject…</Button>
        {live && <Button onClick={() => open('Revoked')}>Revoke…</Button>}
      </Space>
      {!release.eligible && (
        <Text type="secondary" style={{ display: 'block', fontSize: 12, marginBottom: 12 }}>
          Approval needs every item above. An exception does not waive them; it records which are unmet, why release
          goes ahead anyway, until when, and what will be done.
        </Text>
      )}

      <Text strong>Decisions</Text>
      {release.history.length === 0 ? (
        <Text type="secondary" style={{ display: 'block' }}>No release decision yet.</Text>
      ) : (
        <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
          {release.history.map((d) => (
            <li key={d.id} style={{ marginBottom: 6 }}>
              <Text>{`${DECISION_WORDS[d.decision]}`}</Text>
              <Text type="secondary">{` · version ${d.candidateHash.slice(0, 10)}${d.candidateHash === release.currentCandidateHash ? ' (this one)' : ''}`}</Text>
              <Text type="secondary" style={{ display: 'block' }}>{d.reason}</Text>
              {d.followUp && <Text type="secondary" style={{ display: 'block' }}>{`Follow-up: ${d.followUp}`}</Text>}
              <Text type="secondary" style={{ display: 'block', fontSize: 12 }}>
                {`${d.byName ?? `user #${d.byUserId}`} · ${dayjs(d.atUtc).format('D MMM YYYY HH:mm')}${d.expiresUtc ? ` · expires ${dayjs(d.expiresUtc).format('D MMM YYYY')}` : ''}`}
              </Text>
            </li>
          ))}
        </ul>
      )}

      <Drawer
        open={deciding != null}
        onClose={() => setDeciding(null)}
        size="min(520px, 100vw)"
        title={
          deciding === 'Approved' ? 'Approve this version for release'
          : deciding === 'Exception' ? 'Record an exceptional release'
          : deciding === 'Rejected' ? 'Reject this version'
          : 'Revoke the release decision'
        }
        extra={
          <Button type="primary" loading={saving} onClick={() => void submit()}>
            Record
          </Button>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {deciding === 'Exception' && (
            <Alert
              type="warning"
              showIcon
              title="Unmet items stay unmet"
              description={release.checklistItems.filter((i) => !i.met).map((i) => i.requirement).join(' · ')}
            />
          )}
          <div>
            <Text strong>Why</Text>
            <Input.TextArea rows={4} maxLength={4000} value={reason} onChange={(e) => setReason(e.target.value)} />
          </div>
          {deciding === 'Exception' && (
            <>
              <div>
                <Text strong style={{ display: 'block' }}>Expires (at most 30 days)</Text>
                <DatePicker
                  value={expires}
                  onChange={(d) => setExpires(d)}
                  disabledDate={(d) => d.isBefore(dayjs(), 'day') || d.isAfter(dayjs().add(30, 'day'), 'day')}
                />
              </div>
              <div>
                <Text strong>Follow-up</Text>
                <Input.TextArea rows={3} maxLength={4000} placeholder="What will be done so the exception is no longer needed" value={followUp} onChange={(e) => setFollowUp(e.target.value)} />
              </div>
            </>
          )}
        </div>
      </Drawer>
    </SectionCard>
  );
});
